import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Form, Button, Spinner, InputGroup, Badge, Pagination, Collapse } from 'react-bootstrap';
import { FaUserFriends, FaSearch, FaChevronDown, FaChevronUp } from "react-icons/fa";
import api from '../../service/api';
import { useToast } from '../../contexts/ToastContext';

const AllVoters = () => {
  const [voters, setVoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const { showError } = useToast();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVoters, setTotalVoters] = useState(0);

  useEffect(() => {
    fetchVoters();
  }, [currentPage, statusFilter]);

  const fetchVoters = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/voters', {
        params: { page: currentPage, limit: 10, search, status: statusFilter }
      });
      setVoters(res.data.voters || []);
      setTotalPages(res.data.pagination?.pages || 1);
      setTotalVoters(res.data.pagination?.total || 0);
    } catch (error) {
      console.error('Voters fetch error:', error);
      showError(`Failed to fetch voters data: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const executeSearch = () => {
    setCurrentPage(1);
    fetchVoters();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      executeSearch();
    }
  };

  const handlePageChange = (pageNo) => {
    setCurrentPage(pageNo);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'completed': return <Badge bg="success" className="bg-opacity-10 text-success text-uppercase rounded-pill px-3 py-1.5 border" style={{borderColor: 'rgba(25,135,84,0.2)'}}>Completed</Badge>;
      case 'unreachable': return <Badge bg="danger" className="bg-opacity-10 text-danger text-uppercase rounded-pill px-3 py-1.5 border" style={{borderColor: 'rgba(220,53,69,0.2)'}}>Unreachable</Badge>;
      case 'call_back': return <Badge bg="warning" className="bg-opacity-10 text-warning text-uppercase rounded-pill px-3 py-1.5 border" style={{borderColor: 'rgba(255,193,7,0.2)'}}>Call Back</Badge>;
      default: return <Badge bg="secondary" className="bg-opacity-10 text-secondary text-uppercase rounded-pill px-3 py-1.5 border" style={{borderColor: 'rgba(108,117,125,0.2)'}}>Pending</Badge>;
    }
  };

  const toggleExpand = (id) => {
    setExpandedId((current) => (current === id ? null : id));
  };

  return (
    <div className="py-4 px-3 bg-light fade-in-up">
      <Container fluid className="px-0 px-md-2">

        <div className="bg-white rounded-3 shadow-sm border p-4 mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h1 className="h2 fw-bold text-dark mb-1">All Voters</h1>
            <p className="text-secondary mb-0 small">
              View and filter all voters and their contact statuses
            </p>
          </div>
          
          <div className="d-flex align-items-center">
            <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary me-3" style={{ width: "48px", height: "48px" }}>
              <FaUserFriends size={20} />
            </div>
            <div>
              <div className="h4 fw-bold text-dark mb-0">{totalVoters.toLocaleString()}</div>
              <div className="text-secondary small fw-bold text-uppercase ls-wider">Total Voters</div>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Header className="bg-white border-bottom p-4 d-flex gap-3 flex-wrap">
            <InputGroup className="bg-light rounded-3 overflow-hidden border flex-grow-1" style={{ minWidth: "220px", maxWidth: "500px" }}>
              <InputGroup.Text className="bg-light border-0 text-secondary">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search by name, election card, phone, assembly..."
                className="bg-light border-0 shadow-none"
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
              <Button variant="primary" onClick={executeSearch} className="rounded-end border-0 px-4">Search</Button>
            </InputGroup>

            <Form.Select 
              className="bg-light border shadow-none rounded-3" 
              style={{ width: "220px" }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Call Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="call_back">Call Back</option>
              <option value="unreachable">Unreachable</option>
            </Form.Select>
          </Card.Header>

          <div className="voters-table-wrap">
            <Table borderless hover className="mb-0 custom-enterprise-table">
              <thead className="bg-primary bg-opacity-10">
                <tr>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Voters Name</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Election Card No</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Mobile</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Assembly</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Booth / Ward</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider">Polling station</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Society</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider text-nowrap">Res. No</th>
                  <th className="px-3 py-3 text-secondary fw-bold small text-uppercase ls-wider">Status</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                    </td>
                  </tr>
                ) : voters.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5 text-secondary">
                      No voters found matching your search.
                    </td>
                  </tr>
                ) : (
                  voters.map((u) => {
                    const isOpen = expandedId === u._id;
                    return (
                      <React.Fragment key={u._id}>
                        <tr className="hover-row align-middle border-bottom">
                          <td className="px-3 py-3 fw-bold text-dark text-nowrap">
                            {u.firstName} {u.lastName}
                          </td>
                          <td className="px-3 py-3">
                            <Badge bg="dark" className="bg-opacity-10 text-dark rounded-pill px-3 py-1 font-monospace border shadow-xs">
                              {u.epicNo || 'N/A'}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 text-secondary font-monospace fw-semibold text-nowrap">
                            {u.phone || 'N/A'}
                          </td>
                          <td className="px-3 py-3 text-secondary text-nowrap">
                            {u.acNo || 'N/A'}{u.acName ? ` · ${u.acName}` : ''}
                          </td>
                          <td className="px-3 py-3 text-secondary text-nowrap">
                            {u.booth || 'N/A'} / {u.ward || 'N/A'}
                          </td>
                          <td className="px-3 py-3 text-secondary" style={{ maxWidth: '220px' }}>
                            <div className="text-truncate" title={u.pollingStationName || u.engMathak || ''}>
                              {u.pollingStationName || u.engMathak || 'N/A'}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-secondary text-nowrap">{u.societyName || 'N/A'}</td>
                          <td className="px-3 py-3 text-secondary text-nowrap">{u.resNo || 'N/A'}</td>
                          <td className="px-3 py-3">
                            {getStatusBadge(u.callStatus)}
                          </td>
                          <td className="px-3 py-3">
                            <Button
                              variant="link"
                              className="p-0 text-secondary text-decoration-none"
                              onClick={() => toggleExpand(u._id)}
                              aria-label="Show more voter details"
                            >
                              {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                            </Button>
                          </td>
                        </tr>
                        <tr className="border-bottom">
                          <td colSpan="10" className="p-0">
                            <Collapse in={isOpen}>
                              <div className="bg-light px-4 py-3">
                                <div className="row g-3 small">
                                  <div className="col-md-4">
                                    <div className="text-secondary text-uppercase fw-bold mb-1">Polling station address</div>
                                    <div className="text-dark">{u.pollingStationAddress || 'N/A'}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="text-secondary text-uppercase fw-bold mb-1">Address</div>
                                    <div className="text-dark">{u.address || 'N/A'}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="text-secondary text-uppercase fw-bold mb-1">Eng Address</div>
                                    <div className="text-dark">{u.engAddress || 'N/A'}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="text-secondary text-uppercase fw-bold mb-1">HOUSE_NO</div>
                                    <div className="text-dark">{u.houseNo || 'N/A'}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="text-secondary text-uppercase fw-bold mb-1">eng_house_no</div>
                                    <div className="text-dark">{u.engHouseNo || 'N/A'}</div>
                                  </div>
                                  <div className="col-md-4">
                                    <div className="text-secondary text-uppercase fw-bold mb-1">Assembly no / name</div>
                                    <div className="text-dark">{u.acNo || 'N/A'} · {u.acName || 'N/A'}</div>
                                  </div>
                                </div>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
          
          {!loading && totalPages > 1 && (
            <div className="p-3 bg-white border-top d-flex justify-content-center">
              <Pagination className="mb-0">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNumber = idx + 1;
                  
                  if (pageNumber === 1 || pageNumber === totalPages || Math.abs(currentPage - pageNumber) <= 2) {
                    return (
                      <Pagination.Item 
                        key={pageNumber} 
                        active={pageNumber === currentPage}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Pagination.Item>
                    );
                  } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                    return <Pagination.Ellipsis key={pageNumber} disabled />;
                  }
                  return null;
                })}
                
                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </Card>
      </Container>
    </div>
  );
};

export default AllVoters;
